import React, { forwardRef } from "react";

import { twMerge, type TypographyVariant } from "@/design-system/twMerge";

export type { TypographyVariant };

export type TypographyColor =
  | "default"
  | "subtle"
  | "highEmphasis"
  | "mediumEmphasis"
  | "error";

export type TypographyProps = React.HTMLAttributes<HTMLElement> & {
  variant: TypographyVariant;
  as?: React.ElementType;
  color?: TypographyColor;
  className?: string;
  children?: React.ReactNode;
};

const colorClasses: Record<TypographyColor, string> = {
  default: "text-textDefault",
  subtle: "text-textSubtle",
  highEmphasis: "text-textHighEmphasis",
  mediumEmphasis: "text-textMediumEmphasis",
  error: "text-textError",
};

const variantStyles: Record<TypographyVariant, string> = {
  "display-sm": "text-display-sm text-textHighEmphasis",
  "display-md": "text-display-md text-textHighEmphasis",
  "display-lg": "text-display-lg text-textHighEmphasis",
  "display-xl": "text-display-xl text-textHighEmphasis",
  "heading-xs": "text-heading-xs text-textHighEmphasis",
  "heading-sm": "text-heading-sm text-textHighEmphasis",
  "heading-md": "text-heading-md text-textHighEmphasis",
  "heading-lg": "text-heading-lg text-textHighEmphasis",
  "heading-xl": "text-heading-xl text-textHighEmphasis",
  "heading-xxl": "text-heading-xxl text-textHighEmphasis",
  "body-xs": "text-body-xs text-textDefault",
  "body-sm": "text-body-sm text-textDefault",
  "body-md": "text-body-md text-textDefault",
  "body-lg": "text-body-lg text-textDefault",
  "label-xs": "text-label-xs text-textDefault",
  "label-sm": "text-label-sm text-textDefault",
  "label-md": "text-label-md text-textDefault",
  "label-lg": "text-label-lg text-textDefault",
  "eyebrow-xs": "text-eyebrow-xs text-textSubtle",
  "eyebrow-sm": "text-eyebrow-sm text-textSubtle",
  "eyebrow-md": "text-eyebrow-md text-textSubtle",
  "eyebrow-lg": "text-eyebrow-lg text-textSubtle",
};

const defaultTag: Record<TypographyVariant, React.ElementType> = {
  "display-sm": "h1",
  "display-md": "h1",
  "display-lg": "h1",
  "display-xl": "h1",
  "heading-xs": "h4",
  "heading-sm": "h4",
  "heading-md": "h3",
  "heading-lg": "h3",
  "heading-xl": "h2",
  "heading-xxl": "h1",
  "body-xs": "p",
  "body-sm": "p",
  "body-md": "p",
  "body-lg": "p",
  "label-xs": "span",
  "label-sm": "span",
  "label-md": "span",
  "label-lg": "span",
  "eyebrow-xs": "span",
  "eyebrow-sm": "span",
  "eyebrow-md": "span",
  "eyebrow-lg": "span",
};

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ variant, as, color, className, children, ...rest }, ref) => {
    const Component = as ?? defaultTag[variant];

    return (
      <Component
        ref={ref}
        className={twMerge(
          variantStyles[variant],
          color && colorClasses[color],
          className,
        )}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

Typography.displayName = "Typography";
